/**
 * BlogCarousel component that displays a carousel of blog entries.
 *
 * @function BlogCarousel
 * @param {Object} props - The component's props.
 * @param {Object} props.sanity - The data from the sanity CMS.
 * @param {string} props.sanity.backgroundType - The type of background ('Solid', 'Image', 'Gradient').
 * @param {string} props.sanity.backgroundColor - The color for the solid background.
 * @param {Object} props.sanity.backgroundImage - The image for the image background.
 * @param {Object} props.sanity.backgroundGradient - The gradient details for the gradient background.
 * @param {Array} props.sanity.listBlogs - An array of blog objects to display in the carousel.
 * @result {JSX.Element} A section element with class 'elibrero-blog', containing:
 * - A dynamically styled background based on the provided background properties.
 * - A container for the blog entries with optional featured image, tag, title, excerpt, and link to the full blog entry.
 * - Each blog card is styled with specific class names, background images, tags, and additional visual elements.
 * @example
 * <BlogCarousel sanity={sanityData} />
 */
import useEmblaCarousel from 'embla-carousel-react';
import {PortableText} from '@portabletext/react';
import {Link} from '@shopify/hydrogen';

export default function BlogCarousel({sanity}) {
  const blogs = sanity.listBlogs;
  const [viewportRef, embla] = useEmblaCarousel({
    skipSnaps: true,
    align: 'start',
  });
  return (
    <section
      className="elibrero-blog pb-14 pt-5"
      style={{
        background:
          sanity.backgroundType == 'Solid'
            ? sanity.backgroundColor
            : sanity.backgroundType == 'Image'
            ? `url(${sanity.backgroundImage.asset.url})`
            : sanity.backgroundType == 'Gradient'
            ? `linear-gradient(90deg, ${sanity.backgroundGradient.gradientFrom} 0%, ${sanity.backgroundGradient.gradientTo} 100%)`
            : '#00000',
        backgroundSize: 'cover',
      }}
    >
      <div className="container md:mx-auto xs:overflow-hidden ">
        <div className="md:flex xs:flex-col">
          {/* <div className="mt-8 md:pl-28 md:pr-10 flex align-center justify-center flex-col flex-wrap">
            <div>
              <h2 className="section-title text-3xl font-bold">
                {sanity.title}
              </h2>
              <p className="my-4">{sanity.description}</p>
              <Link
                className="btn-main bg-purple-120 inline text-white font-semibold rounded-full"
                to="/blogs"
              >
                {' '}
                Ir al Blog
              </Link>
            </div>
          </div> */}
          <div className="md:w-9/12 mx-auto xs:w-full">
            <div className="overflow-hidden">
              <div className="relative flex gap-4 " ref={viewportRef}>
                <div className="my-5 embla__container flex gap-4 ">
                  {blogs
                    ? blogs.map((item, index) => {
                        return (
                          <div
                            key={item._id + index}
                            className="w-1/4 md:w-1/2 xs:w-full blog_card relative"
                          >
                            <div
                              className="card relative shadow-sm bg-white overflow-hidden"
                              key={item._id}
                              style={{height: '350px'}}
                            >
                              <div className="relative">
                                {item?.featuredImage != null && (
                                  <div
                                    className="relative"
                                    style={{
                                      backgroundSize: 'contain !important',
                                      minWidth: '227px  ',
                                      minHeight: '150px',
                                      background: `url(${item.featuredImage.asset?.url}) no-repeat`,
                                    }}
                                  >
                                    {item?.blogTags &&
                                    item.blogTags.length != 0 ? (
                                      <div className="card-tag absolute top-0 text-sm text-white left-2 rounded-full bg-purple-120 px-8 py-2  mt-2 mr-2 font-semibold inline">
                                        {item.blogTags[0].title}
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </div>

                              <div className="p-4 bg-white rounded-br-md rounded-bl-md overflow-hidden">
                                <h3 className="font-semibold text-purple-120">
                                  {item.title}
                                </h3>
                                {item.excerpt && (
                                <div className="block text-xs">
                                  {item.excerpt.length > 100
                                    ? item.excerpt.slice(0, 100) + '...'
                                    : item.excerpt}
                                </div>
                                )}
                              </div>
                              <div className="text-right bottom-0 absolute w-full px-4 pb-2">
                                <Link
                                  to={`/blog/${item.slug && item.slug.current}`}
                                  className="text-xs text-blue-55 underline font-semibold"
                                >
                                  Ver nota completa
                                </Link>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    : ''}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
